"use strict";
/**
 * @remarks
 * @example How to use command queue?
 * ```html
 * <script>
 * // init global object
 * window.ph=window.ph||function(){(ph.q=ph.q||[]).push(arguments)};ph.l=+new Date;
 *
 * //dispatch command to the global object
 * ph('salesforce.lead.send');
 * </script>
 * ```
 *
 * by default 'ph' is used as global object
 * we can use PivotHealthAnalyticsObject to rename the object
 *
 * @example Rename PivotHealthAnalyticsObject
 * ```ts
 * window.PivotHealthAnalyticsObject = 'anotherName'
 * window.anotherName=window.anotherName||function(){(anotherName.q=anotherName.q||[]).push(arguments)};anotherName.l=+new Date;
 * anotherName('command');
 * ```
 *
 * @packageDocumentation
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createCommandQueue = void 0;
const is_array_1 = require("../../validation/is-array/is-array");
const is_function_1 = require("../../validation/is-function/is-function");
const is_string_1 = require("../../validation/is-string/is-string");
/**
 * @internal
 *
 * @param commandLog - Dictionary of commands registered
 * @param args - Array of arguments passed to the global object
 *
 */
function executeCommand(commandLog, [command, ...arg]) {
    if ((0, is_function_1.isFunction)(command) && commandLog['default']) {
        commandLog['default'](command, ...arg);
    }
    if ((0, is_string_1.isString)(command) && commandLog[command]) {
        commandLog[command](...arg);
    }
}
/**
 * Create a command queue to execute commands from the global object
 * @public
 *
 * @param defaultName - Global object name used to interact with the commands
 *
 * @returns CommandQueue instance
 *
 * @example
 * ```ts
 * import { createCommandQueue } from '@hc-healthcare/utils'
 *
 * const { runCommands, registerCommand, globalObject } = createCommandQueue('ph');
 *
 * registerCommand('salesforce.lead.send', salesforceLeadSendCommand);
 * registerCommand('salesforce.opportunity.send', salesforceOpportunitySendCommand);
 *
 * // command to execute when called as follows ph(() => {})
 * registerCommand('default', defaultCommand);
 *
 * runCommands();
 * ```
 */
function createCommandQueue(defaultName = 'ph') {
    const globalPivotObjectName = window.PivotHealthAnalyticsObject;
    const globalObjectName = (0, is_string_1.isString)(globalPivotObjectName)
        ? globalPivotObjectName.replace(/^[\s\xa0]+|[\s\xa0]+$/g, '') // remove any dangerous whitespace
        : defaultName;
    // TODO: check if this is the correct way to handle the global object
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!(0, is_function_1.isFunction)(window[globalObjectName])) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window[globalObjectName] = function (...arg) {
            globalObject.q.push(arg);
        };
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const globalObject = window[globalObjectName];
    const isArr = (0, is_array_1.isArray)(globalObject.q);
    globalObject.q = isArr ? globalObject.q : [];
    const commandLog = {};
    return {
        globalObject,
        registerCommand(commandName, handleCommand) {
            commandLog[commandName] = handleCommand;
        },
        runCommands() {
            for (const arg of globalObject.q) {
                executeCommand(commandLog, arg);
            }
            globalObject.q = {
                push: function push(args) {
                    executeCommand(commandLog, args);
                },
            };
        },
    };
}
exports.createCommandQueue = createCommandQueue;
//# sourceMappingURL=command-queue.js.map